<template>
  <div class="coupon-lotto-page">
    <!-- Last Result Banner (shows in new period if hasn't dismissed) -->
    <div
      v-if="showLastResultBanner && lastResult"
      class="last-result-banner"
      :class="{ won: lastResult.hasWon }"
      @click="showResultDetail = true"
    >
      <div class="banner-content">
        <span class="banner-icon">{{ lastResult.hasWon ? '🎉' : '📢' }}</span>
        <div class="banner-text">
          <span class="banner-title"
            >ผลงวดที่แล้ว ({{ formatDate(lastResult.periodDate) }})</span
          >
          <span class="banner-result">
            เลขที่ออก: <strong>{{ lastResult.winningNumber }}</strong>
            <template
              v-if="
                lastResult.mySelections && lastResult.mySelections.length > 0
              "
            >
              - เลือก {{ lastResult.mySelections.length }} เลข
              <template v-if="lastResult.hasWon"
                >, ถูก {{ lastResultWonCount }} เลข</template
              >
            </template>
          </span>
        </div>
      </div>
      <span
        v-if="lastResult.unclaimedCount > 0"
        class="banner-claim-badge"
        @click.stop="showResultDetail = true"
      >
        💰 รับรางวัล
      </span>
    </div>

    <!-- Waiting Period View (period ended, awaiting draw) -->
    <div v-if="isWaitingPeriod" class="waiting-period-view">
      <div class="waiting-header">
        <h1 class="waiting-title">⏳ รอประกาศผล ⏳</h1>
        <p class="waiting-subtitle">
          งวดวันที่ {{ formatDate(period.drawDate) }}
        </p>
      </div>
      <div class="waiting-content">
        <div class="waiting-animation">
          <span class="waiting-spinner-icon"></span>
        </div>
        <p class="waiting-message">กำลังรอผลหวยจากแอดมิน</p>
        <p class="waiting-hint">ผลจะประกาศเร็วๆ นี้</p>
      </div>

      <!-- My Selections in waiting -->
      <div v-if="mySelections.length > 0" class="waiting-selections">
        <h3 class="section-title">📋 เลขที่เลือกในงวดนี้</h3>
        <div class="selections-grid">
          <div
            v-for="selection in mySelections"
            :key="selection.id"
            class="selection-badge status-pending"
          >
            <span class="badge-number">{{ selection.number }}</span>
            <span class="badge-status">⏳</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Result Period View (Gap Time) -->
    <div v-else-if="isResultPeriod && currentResult" class="result-period-view">
      <div class="result-header">
        <h1 class="result-title">🎊 ประกาศผลรางวัล 🎊</h1>
        <p class="result-period-date">
          งวดวันที่ {{ formatDate(currentResult.periodDate) }}
        </p>
      </div>

      <div class="winning-number-section">
        <h2 class="winning-label">✨ เลขที่ออก ✨</h2>
        <div class="winning-number-display">
          <span class="winning-number">{{ currentResult.winningNumber }}</span>
        </div>
      </div>

      <div class="my-result-section">
        <h3 class="my-result-title">📋 ผลออกเลขรางวัล</h3>
        <div
          v-if="
            currentResult.mySelections && currentResult.mySelections.length > 0
          "
          class="my-result-card"
          :class="{ won: currentResultHasWon }"
        >
          <!-- Grid of all selections -->
          <div class="my-selections-label">เลขที่คุณเลือก:</div>
          <div class="result-selections-grid">
            <div
              v-for="selection in currentResult.mySelections"
              :key="selection.id"
              class="result-selection-item"
              :class="getResultSelectionClass(selection)"
            >
              <span class="item-number">{{ selection.number }}</span>
              <span class="item-status">{{
                getResultSelectionIcon(selection)
              }}</span>
            </div>
          </div>

          <!-- Result summary -->
          <div v-if="currentResultHasWon" class="result-message won">
            <span class="result-icon">🎉</span>
            <div class="result-text">
              <span class="result-main">ยินดีด้วย! คุณถูกรางวัล</span>
              <div class="prize-breakdown">
                <div class="prize-row">
                  <span class="prize-label">🏆 รางวัลรวม:</span>
                  <span class="prize-value"
                    >฿{{ formatNumber(currentResult.prize) }}</span
                  >
                </div>
                <div class="prize-row">
                  <span class="prize-label">👥 ผู้ถูกรางวัล:</span>
                  <span class="prize-value"
                    >{{ currentResult.totalWinners || 1 }} คน</span
                  >
                </div>
                <div class="prize-row highlight">
                  <span class="prize-label">💰 รางวัลที่ได้รับ:</span>
                  <span class="prize-value"
                    >฿{{ formatNumber(currentResultPrizePerWinner) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div v-else class="result-message lost">
            <span class="result-icon">😢</span>
            <div class="result-text">
              <span class="result-main">เสียใจด้วย ไม่ถูกรางวัล</span>
              <span class="result-note">ลองใหม่งวดหน้านะ!</span>
            </div>
          </div>

          <!-- Claim button if has unclaimed -->
          <button
            v-if="currentResultUnclaimedCount > 0"
            class="claim-btn"
            :disabled="isClaiming"
            @click="claimAllPrizes(currentResult.mySelections)"
          >
            <span v-if="isClaiming" class="btn-loading">
              <span class="spinner"></span> กำลังรับรางวัล...
            </span>
            <span v-else>💰 รับรางวัล</span>
          </button>

          <!-- Already claimed message -->
          <div
            v-if="currentResultHasWon && currentResultUnclaimedCount === 0"
            class="claimed-message"
          >
            ✅ รับรางวัลแล้ว (เครดิตเข้าบัญชีแล้ว)
          </div>
        </div>
        <div v-else class="no-selection-card">
          <span class="no-selection-icon">📝</span>
          <span class="no-selection-text">คุณไม่ได้เลือกเลขในงวดนี้</span>
        </div>
      </div>

      <div v-if="nextPeriod" class="next-period-section">
        <span class="next-period-icon">⏰</span>
        <span class="next-period-text"
          >งวดถัดไปเริ่ม {{ formatDate(nextPeriod.startDate) }} เวลา 00:00</span
        >
      </div>
    </div>

    <!-- Active Period View -->
    <div
      v-else-if="period && period.status === 'active'"
      class="active-period-view"
    >
      <!-- Period Header -->
      <div class="period-header-card">
        <button class="info-btn" @click="showRulesModal = true">
          <feather-icon icon="InfoIcon" size="20" />
        </button>
        <div class="period-header-content">
          <h1 class="period-title">🎫 คูปองหวย ลุ้นโชค</h1>
          <div class="period-info">
            <div class="period-info-left">
              <span class="period-date"
                >งวดวันที่ {{ formatDate(period.drawDate) }}</span
              >
              <span class="period-prize"
                >🏆 รางวัล ฿{{ formatNumber(period.prize) }}</span
              >
            </div>
            <div class="period-countdown">
              <span class="countdown-label">⏱️ เหลือเวลา</span>
              <div class="countdown-timer">
                <div v-if="countdown.days > 0" class="countdown-unit">
                  <span class="countdown-value">{{ countdown.days }}</span>
                  <span class="countdown-text">วัน</span>
                </div>
                <div class="countdown-unit">
                  <span class="countdown-value">{{
                    String(countdown.hours).padStart(2, '0')
                  }}</span>
                  <span class="countdown-text">ชม.</span>
                </div>
                <div class="countdown-unit">
                  <span class="countdown-value">{{
                    String(countdown.minutes).padStart(2, '0')
                  }}</span>
                  <span class="countdown-text">นาที</span>
                </div>
                <div class="countdown-unit">
                  <span class="countdown-value">{{
                    String(countdown.seconds).padStart(2, '0')
                  }}</span>
                  <span class="countdown-text">วิ</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Selection Form or Already Selected State -->
      <div class="selection-section">
        <!-- Has Coupon - Can Select -->
        <div
          v-if="coupons.available > 0 && !hasSelectedToday"
          class="selection-form-card"
        >
          <h2 class="form-title">🎰 เลือกเลขนำโชค</h2>
          <p class="coupon-count">
            คูปองคงเหลือ: <strong>{{ coupons.available }}</strong> ใบ
          </p>

          <div class="number-input-wrapper">
            <div class="number-input-container">
              <input
                v-model="inputDigit1"
                type="text"
                maxlength="1"
                pattern="[0-9]"
                inputmode="numeric"
                class="digit-input"
                placeholder="0"
                ref="digit1"
                @input="onDigit1Input"
                @keydown="onDigitKeydown($event, 1)"
              />
              <input
                v-model="inputDigit2"
                type="text"
                maxlength="1"
                pattern="[0-9]"
                inputmode="numeric"
                class="digit-input"
                placeholder="0"
                ref="digit2"
                @input="onDigit2Input"
                @keydown="onDigitKeydown($event, 2)"
              />
            </div>
            <p class="input-hint">ระบุเลข 2 หลัก (00-99)</p>
          </div>

          <p v-if="isAlreadySelectedNumber" class="duplicate-warning">
            ⚠️ คุณเลือกเลขนี้ไปแล้วในงวดนี้
          </p>
          <button
            class="submit-btn"
            :disabled="
              !isValidNumber || isSubmitting || isAlreadySelectedNumber
            "
            @click="submitNumber"
          >
            <span v-if="isSubmitting" class="btn-loading">
              <span class="spinner"></span>
              กำลังส่ง...
            </span>
            <span v-else>✨ ยืนยันเลือกเลข</span>
          </button>
        </div>

        <!-- Already Selected Today -->
        <div v-else-if="hasSelectedToday" class="already-selected-card">
          <h2 class="card-title">✅ เลือกเลขแล้ว</h2>
          <p class="card-message">วันนี้คุณเลือกเลขแล้ว</p>
          <p class="card-hint">
            ฝากเงินอีก 300 บาท<br />เพื่อรับคูปองวันพรุ่งนี้
          </p>
          <button class="deposit-btn" @click="goToDeposit">
            💰 ฝากเงินเลย
          </button>
        </div>

        <!-- No Coupon Available -->
        <div v-else class="no-coupon-card">
          <h2 class="card-title">🎫 ไม่มีคูปอง</h2>
          <p class="card-message">คุณยังไม่มีคูปองสำหรับเลือกเลข</p>
          <p class="card-hint">ฝากเงิน 300 บาท<br />เพื่อรับคูปอง 1 ใบต่อวัน</p>
          <button class="deposit-btn" @click="goToDeposit">
            💰 ฝากเงินเลย
          </button>
        </div>
      </div>

      <!-- My Selections Grid -->
      <div class="my-selections-section">
        <div class="section-header">
          <h3 class="section-title">📋 เลขที่เลือกในงวดนี้</h3>
          <span v-if="mySelections.length > 0" class="selection-count">
            {{ mySelections.length }} เลข
          </span>
        </div>
        <div v-if="mySelections.length > 0" class="selections-grid">
          <div
            v-for="selection in mySelections"
            :key="selection.id"
            class="selection-badge"
            :class="getSelectionStatusClass(selection)"
            @click="showSelectionDetail(selection)"
          >
            <span class="badge-number">{{ selection.number }}</span>
            <span class="badge-status">{{
              getSelectionStatusIcon(selection)
            }}</span>
          </div>
        </div>
        <div v-else class="no-selections">
          <span class="no-selections-text">(ยังไม่มีเลขที่เลือก)</span>
        </div>
        <div v-if="mySelections.length > 0" class="selections-legend">
          <span class="legend-item">
            <span class="legend-icon">⏳</span> รอประกาศ
          </span>
          <span class="legend-item won">
            <span class="legend-icon">🎉</span> ถูกรางวัล
          </span>
          <span class="legend-item claimed">
            <span class="legend-icon">✅</span> รับแล้ว
          </span>
          <span class="legend-item lost">
            <span class="legend-icon">😢</span> ไม่ถูก
          </span>
        </div>
      </div>

      <!-- Selection Detail Modal -->
      <div
        v-if="selectedSelectionDetail"
        class="modal-overlay"
        @click="selectedSelectionDetail = null"
      >
        <div class="selection-detail-modal" @click.stop>
          <button class="modal-close" @click="selectedSelectionDetail = null">
            ×
          </button>
          <div class="detail-number">{{ selectedSelectionDetail.number }}</div>
          <div class="detail-info">
            <div class="detail-row">
              <span class="detail-label">เลือกเมื่อ:</span>
              <span class="detail-value">{{
                formatDate(selectedSelectionDetail.selectedAt)
              }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">รอผลวันที่:</span>
              <span class="detail-value">{{
                formatDate(period.drawDate)
              }}</span>
            </div>
            <div
              class="detail-status"
              :class="getSelectionStatusClass(selectedSelectionDetail)"
            >
              <span class="status-icon">{{
                getSelectionStatusIcon(selectedSelectionDetail)
              }}</span>
              <span class="status-text">{{
                getSelectionStatusText(selectedSelectionDetail)
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Last Winners Section -->
      <div v-if="lastWinners" class="last-winner-section">
        <h3 class="section-title">🏆 ผลรางวัลงวดล่าสุด</h3>
        <div class="last-winner-card">
          <div class="winner-period">
            งวด {{ formatDate(lastWinners.periodDate) }}
          </div>
          <div class="winner-number-row">
            <span class="winner-label">เลขที่ออก:</span>
            <span class="winner-number">{{ lastWinners.winningNumber }}</span>
            <span class="winner-icon">🎊</span>
          </div>
          <div
            v-if="lastWinners.winners && lastWinners.winners.length > 0"
            class="winners-list"
          >
            <div class="winners-label">
              ผู้โชคดี ({{ lastWinners.winners.length }} คน):
            </div>
            <div class="winners-names">
              <span
                v-for="(winner, idx) in lastWinners.winners"
                :key="idx"
                class="winner-name-badge"
                >{{ winner.memberName }}</span
              >
            </div>
          </div>
          <div v-else class="winner-name">ไม่มีผู้ถูกรางวัล</div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div
      v-if="showConfirmModal"
      class="modal-overlay"
      @click="closeConfirmModal"
    >
      <div class="confirm-modal" @click.stop>
        <h3 class="modal-title">ยืนยันเลือกเลข</h3>
        <div class="modal-number">{{ selectedNumber }}</div>
        <p class="modal-message">
          คุณต้องการเลือกเลข <strong>{{ selectedNumber }}</strong> ใช่หรือไม่?
        </p>
        <p class="modal-warning">⚠️ เลือกได้ 1 ครั้งต่อวันเท่านั้น</p>
        <div class="modal-actions">
          <button class="btn-cancel" @click="closeConfirmModal">ยกเลิก</button>
          <button
            class="btn-confirm"
            :disabled="isSubmitting"
            @click="confirmSelection"
          >
            <span v-if="isSubmitting" class="spinner"></span>
            <span v-else>ยืนยัน</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Result Detail Modal (from banner tap) -->
    <div
      v-if="showResultDetail && lastResult"
      class="modal-overlay"
      @click="showResultDetail = false"
    >
      <div class="result-detail-modal" @click.stop>
        <button class="modal-close" @click="showResultDetail = false">×</button>
        <h3 class="modal-title">ผลรางวัลงวดที่แล้ว</h3>
        <p class="modal-period">{{ formatDate(lastResult.periodDate) }}</p>
        <div class="modal-winning-number">
          <span class="label">เลขที่ออก</span>
          <span class="number">{{ lastResult.winningNumber }}</span>
        </div>

        <!-- My Selections Grid from Last Period -->
        <div
          v-if="lastResult.mySelections && lastResult.mySelections.length > 0"
          class="modal-selections-section"
        >
          <span class="label"
            >เลขที่คุณเลือก ({{ lastResult.mySelections.length }} เลข)</span
          >
          <div class="modal-selections-grid">
            <div
              v-for="selection in lastResult.mySelections"
              :key="selection.id"
              class="modal-selection-item"
              :class="getSelectionStatusClass(selection)"
            >
              <span class="item-number">{{ selection.number }}</span>
              <span class="item-status">{{
                getSelectionStatusIcon(selection)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Result Summary -->
        <div class="modal-result-summary" :class="{ won: lastResult.hasWon }">
          <template v-if="lastResult.hasWon">
            <span class="summary-title">🎉 ยินดีด้วย! ถูกรางวัล</span>
            <div class="prize-breakdown-mini">
              <span>รางวัลรวม ฿{{ formatNumber(lastResult.prize) }}</span>
              <span>÷ {{ lastResult.totalWinners || 1 }} คน</span>
              <span class="highlight"
                >= ฿{{ formatNumber(lastResultPrizePerWinner) }}/คน</span
              >
            </div>
          </template>
          <span v-else>😢 ไม่ถูกรางวัล - ลองใหม่งวดนี้!</span>
        </div>

        <!-- Claim Button for Unclaimed Wins -->
        <div
          v-if="lastResultUnclaimedSelections.length > 0"
          class="modal-claim-section"
        >
          <p class="claim-info">
            คุณมี
            {{ lastResultUnclaimedSelections.length }} รางวัลที่ยังไม่ได้รับ
          </p>
          <button
            class="claim-all-btn"
            :disabled="isClaimingLastResult"
            @click="claimLastResultPrize"
          >
            <span v-if="isClaimingLastResult" class="btn-loading">
              <span class="spinner"></span> กำลังรับรางวัล...
            </span>
            <span v-else>
              💰 รับรางวัล (฿{{ formatNumber(lastResultPrizePerWinner) }})
            </span>
          </button>
        </div>

        <!-- Already Claimed Message -->
        <div
          v-else-if="
            lastResult.hasWon && lastResultUnclaimedSelections.length === 0
          "
          class="modal-claimed-message"
        >
          ✅ รับรางวัลเรียบร้อยแล้ว
        </div>
      </div>
    </div>

    <!-- Rules & Conditions Modal -->
    <div
      v-if="showRulesModal"
      class="modal-overlay"
      @click="showRulesModal = false"
    >
      <div class="rules-modal" @click.stop>
        <button class="modal-close" @click="showRulesModal = false">×</button>
        <h2 class="rules-title">📜 กติกาและเงื่อนไข</h2>
        <div class="rules-content">
          <div class="rule-item">
            <span class="rule-icon">🎫</span>
            <div class="rule-text">
              <strong>การได้รับคูปอง</strong>
              <p>ฝากเงินขั้นต่ำ 300 บาท/วัน รับคูปอง 1 ใบต่อวัน</p>
            </div>
          </div>
          <div class="rule-item">
            <span class="rule-icon">🔢</span>
            <div class="rule-text">
              <strong>การเลือกเลข</strong>
              <p>เลือกเลข 2 หลัก (00-99) ใช้คูปอง 1 ใบต่อ 1 เลข</p>
            </div>
          </div>
          <div class="rule-item">
            <span class="rule-icon">🏆</span>
            <div class="rule-text">
              <strong>รางวัล</strong>
              <p>
                รางวัลรวม ฿{{ formatNumber(period.prize || 2000) }} หาร
                จำนวนคนถูก
              </p>
            </div>
          </div>
          <div class="rule-item">
            <span class="rule-icon">💰</span>
            <div class="rule-text">
              <strong>การรับรางวัล</strong>
              <p>ทำ 1เทิร์น ถอนสูงสุดตามรางวัลที่ได้รับ</p>
            </div>
          </div>
        </div>
        <button class="rules-close-btn" @click="showRulesModal = false">
          เข้าใจแล้ว
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// Cache number formatter outside component for performance
const numberFormatter = new Intl.NumberFormat('th-TH')

export default {
  name: 'CouponLottoPage',
  data() {
    return {
      isLoading: true,
      isSubmitting: false,
      isClaiming: false,
      isClaimingLastResult: false,
      showConfirmModal: false,
      showResultDetail: false,
      showLastResultBanner: false,
      selectedSelectionDetail: null,
      showRulesModal: false,

      // Input fields for 2-digit number
      inputDigit1: '',
      inputDigit2: '',

      // Period info from API
      period: {
        id: null,
        drawDate: null,
        prize: 1000,
        status: 'active' // active, result, ended
      },

      // Next period info (for result period view)
      nextPeriod: null,

      // Member's coupon info
      coupons: {
        available: 0,
        used: 0
      },

      // Member's selections for current period
      mySelections: [],

      // Current result (for result period)
      currentResult: null,

      // Last period result (for banner in new period)
      lastResult: null,

      // Last winners info (array of winners)
      lastWinners: null,

      // Countdown timer
      countdownInterval: null,
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      // Cached drawDate timestamp for performance
      drawDateTimestamp: null
    }
  },
  computed: {
    selectedNumber() {
      return `${this.inputDigit1 || '0'}${this.inputDigit2 || '0'}`
    },
    isValidNumber() {
      const d1 = this.inputDigit1
      const d2 = this.inputDigit2
      return d1 !== '' && d2 !== '' && /^\d$/.test(d1) && /^\d$/.test(d2)
    },
    hasSelectedToday() {
      if (!this.mySelections.length) return false
      const today = new Date().toDateString()
      return this.mySelections.some(s => {
        const selectionDate = new Date(s.selectedAt).toDateString()
        return selectionDate === today
      })
    },
    isAlreadySelectedNumber() {
      if (!this.isValidNumber) return false
      return this.mySelections.some(s => s.number === this.selectedNumber)
    },
    isResultPeriod() {
      return this.period.status === 'result' && this.currentResult
    },
    isWaitingPeriod() {
      return this.period.status === 'waiting'
    },
    // Computed for lastResult banner helpers
    lastResultWonCount() {
      if (!this.lastResult?.mySelections) return 0
      return this.lastResult.mySelections.filter(
        s => s.status === 'won' || s.status === 'claimed'
      ).length
    },
    lastResultUnclaimedSelections() {
      if (!this.lastResult?.mySelections) return []
      return this.lastResult.mySelections.filter(
        s => s.status === 'won' && !s.claimed
      )
    },
    // Computed for currentResult helpers
    currentResultHasWon() {
      if (!this.currentResult?.mySelections) return false
      return this.currentResult.mySelections.some(
        s => s.status === 'won' || s.status === 'claimed'
      )
    },
    currentResultWonCount() {
      if (!this.currentResult?.mySelections) return 0
      return this.currentResult.mySelections.filter(
        s => s.status === 'won' || s.status === 'claimed'
      ).length
    },
    currentResultUnclaimedCount() {
      if (!this.currentResult?.mySelections) return 0
      return this.currentResult.mySelections.filter(s => s.status === 'won')
        .length
    },
    currentResultPrizePerWinner() {
      if (!this.currentResult || !this.currentResult.totalWinners)
        return this.period.prize
      return Math.floor(
        this.currentResult.prize / this.currentResult.totalWinners
      )
    },
    lastResultPrizePerWinner() {
      if (!this.lastResult || !this.lastResult.totalWinners)
        return this.lastResult?.prize || 1000
      return Math.floor(this.lastResult.prize / this.lastResult.totalWinners)
    }
  },
  mounted() {
    this.fetchLottoStatus()
    // Note: startCountdown is called after fetchLottoStatus completes
    // to ensure we have period data and status before starting timer
  },
  beforeDestroy() {
    this.stopCountdown()
  },
  methods: {
    async fetchLottoStatus() {
      this.isLoading = true
      try {
        const response = await this.$http.get('/member/coupon-lotto/status')
        const { data: responseData } = response
        if (!responseData.success) {
          throw new Error('Failed to fetch status')
        }
        const { data } = responseData

        // Update period info
        if (data.period) {
          this.period = {
            id: data.period.id,
            drawDate: data.period.drawDate,
            prize: data.period.prize || 1000,
            status: data.period.status || 'active'
          }
          // Cache drawDate timestamp for countdown performance
          this.drawDateTimestamp = data.period.drawDate
            ? new Date(data.period.drawDate).getTime()
            : null
        } else {
          this.drawDateTimestamp = null
        }

        // Restart countdown with updated period data
        this.stopCountdown()
        this.startCountdown()

        // Update coupons
        this.coupons = {
          available: data.coupons?.available || 0,
          used: data.coupons?.used || 0
        }

        // Update selections
        this.mySelections = data.mySelections || []

        // Update last result (for banner)
        if (data.lastResult) {
          this.lastResult = data.lastResult
          const dismissedKey = `lotto_result_dismissed_${data.lastResult.periodId}`
          this.showLastResultBanner = !localStorage.getItem(dismissedKey)
        }

        // Update current result (for result period view)
        if (data.currentResult) {
          this.currentResult = data.currentResult
        }

        // Update next period
        if (data.nextPeriod) {
          this.nextPeriod = data.nextPeriod
        }

        // Update last winners
        if (data.lastWinners) {
          this.lastWinners = data.lastWinners
        }
      } catch (error) {
        console.error('Failed to fetch lotto status:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: 'ไม่สามารถโหลดข้อมูลได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isLoading = false
      }
    },

    onDigit1Input(e) {
      const { value } = e.target
      if (!/^\d?$/.test(value)) {
        this.inputDigit1 = value.replace(/\D/g, '').slice(0, 1)
      }
      if (this.inputDigit1.length === 1) {
        this.$refs.digit2.focus()
      }
    },

    onDigit2Input(e) {
      const { value } = e.target
      if (!/^\d?$/.test(value)) {
        this.inputDigit2 = value.replace(/\D/g, '').slice(0, 1)
      }
    },

    onDigitKeydown(e, digit) {
      // Handle backspace navigation
      if (e.key === 'Backspace' && digit === 2 && this.inputDigit2 === '') {
        this.$refs.digit1.focus()
      }
    },

    submitNumber() {
      if (!this.isValidNumber) return
      this.showConfirmModal = true
    },

    closeConfirmModal() {
      this.showConfirmModal = false
    },

    async confirmSelection() {
      if (this.isSubmitting) return

      this.isSubmitting = true
      try {
        const response = await this.$http.post('/member/coupon-lotto/select', {
          number: this.selectedNumber
        })

        const { data } = response
        if (!data.success) {
          throw new Error(data.message || 'Failed to select number')
        }

        // Update local state from response
        if (data.data) {
          this.mySelections.unshift({
            id: data.data.id,
            number: data.data.number,
            selectedAt: data.data.selectedAt,
            status: data.data.status
          })
          if (data.data.coupons) {
            this.coupons.available = data.data.coupons.available
            this.coupons.used = data.data.coupons.used
          }
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'สำเร็จ!',
            text: `เลือกเลข ${this.selectedNumber} เรียบร้อยแล้ว`,
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })

        // Close modal and reset inputs
        this.closeConfirmModal()
        this.inputDigit1 = ''
        this.inputDigit2 = ''
      } catch (error) {
        console.error('Failed to select number:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: error.response?.data?.message || 'ไม่สามารถเลือกเลขได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isSubmitting = false
      }
    },

    async claimPrize() {
      if (
        this.isClaiming ||
        !this.currentResult ||
        !this.currentResult.selectionId
      )
        return

      this.isClaiming = true
      try {
        const response = await this.$http.post('/member/coupon-lotto/claim', {
          selection_id: this.currentResult.selectionId
        })

        const { data } = response
        if (!data.success) {
          throw new Error(data.message || 'Failed to claim prize')
        }

        this.currentResult.claimed = true

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'สำเร็จ!',
            text: 'รับรางวัลเรียบร้อยแล้ว เครดิตเข้าบัญชีแล้ว',
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })
      } catch (error) {
        console.error('Failed to claim prize:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: error.response?.data?.message || 'ไม่สามารถรับรางวัลได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isClaiming = false
      }
    },

    async claimLastResultPrize() {
      if (
        this.isClaimingLastResult ||
        this.lastResultUnclaimedSelections.length === 0
      )
        return

      this.isClaimingLastResult = true
      try {
        const unclaimedSelection = this.lastResultUnclaimedSelections[0]
        const response = await this.$http.post('/member/coupon-lotto/claim', {
          selection_id: unclaimedSelection.id
        })

        const { data } = response
        if (!data.success) {
          throw new Error(data.message || 'Failed to claim prize')
        }

        // Update local state - mark selection as claimed
        if (this.lastResult?.mySelections) {
          this.lastResult.mySelections = this.lastResult.mySelections.map(s => {
            if (s.id === unclaimedSelection.id) {
              return { ...s, claimed: true, status: 'claimed' }
            }
            return s
          })
          this.lastResult.unclaimedCount = 0
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'สำเร็จ!',
            text: 'รับรางวัลเรียบร้อยแล้ว เครดิตเข้าบัญชีแล้ว',
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })

        // Refresh balance
        if (this.$eventBus) {
          this.$eventBus.$emit('refreshBalance')
        }
      } catch (error) {
        console.error('Failed to claim prize:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: error.response?.data?.message || 'ไม่สามารถรับรางวัลได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isClaimingLastResult = false
      }
    },

    dismissLastResultBanner() {
      if (this.lastResult) {
        const dismissedKey = `lotto_result_dismissed_${this.lastResult.periodId}`
        localStorage.setItem(dismissedKey, 'true')
      }
      this.showLastResultBanner = false
    },

    goToDeposit() {
      this.$router.push('/deposit')
    },

    formatNumber(num) {
      return numberFormatter.format(num)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('th-TH', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    },

    getSelectionStatusClass(selection) {
      if (selection.status === 'claimed') return 'status-claimed'
      if (selection.status === 'won') return 'status-won'
      if (selection.status === 'lost') return 'status-lost'
      return 'status-pending'
    },

    getSelectionStatusIcon(selection) {
      if (selection.status === 'claimed') return '✅'
      if (selection.status === 'won') return '🎉'
      if (selection.status === 'lost') return '😢'
      return '⏳'
    },

    getSelectionStatusText(selection) {
      if (selection.status === 'claimed') return 'รับรางวัลแล้ว'
      if (selection.status === 'won') return 'ถูกรางวัล!'
      if (selection.status === 'lost') return 'ไม่ถูก'
      return 'รอประกาศผล'
    },

    showSelectionDetail(selection) {
      this.selectedSelectionDetail = selection
    },

    // Result selection grid helpers
    getResultSelectionClass(selection) {
      if (selection.status === 'claimed') return 'status-claimed'
      if (selection.status === 'won') return 'status-won'
      if (selection.status === 'lost') return 'status-lost'
      return 'status-pending'
    },

    getResultSelectionIcon(selection) {
      if (selection.status === 'claimed') return '✅'
      if (selection.status === 'won') return '🎉'
      if (selection.status === 'lost') return '😢'
      return '⏳'
    },

    startCountdown() {
      // Only run countdown during active period
      if (this.period?.status !== 'active') {
        this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        return
      }
      this.updateCountdown()
      this.countdownInterval = setInterval(() => {
        this.updateCountdown()
      }, 1000)
    },

    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    updateCountdown() {
      // Use cached timestamp for performance (avoid parsing date string every second)
      if (!this.drawDateTimestamp) {
        this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        return
      }

      const diff = this.drawDateTimestamp - Date.now()

      if (diff <= 0) {
        this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      )
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      this.countdown = { days, hours, minutes, seconds }
    },

    async claimAllPrizes(selections) {
      if (this.isClaiming) return

      const unclaimedSelections = selections.filter(
        s => s.status === 'won' && !s.claimed
      )
      if (unclaimedSelections.length === 0) return

      this.isClaiming = true
      try {
        const response = await this.$http.post('/member/coupon-lotto/claim', {
          selection_id: unclaimedSelections[0].id
        })

        const { data } = response
        if (!data.success) {
          throw new Error(data.message || 'Failed to claim prize')
        }

        // Update local state
        if (this.currentResult?.mySelections) {
          this.currentResult.mySelections = this.currentResult.mySelections.map(
            s => {
              if (s.id === unclaimedSelections[0].id) {
                return { ...s, claimed: true, status: 'claimed' }
              }
              return s
            }
          )
          this.currentResult.unclaimedCount = 0
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'สำเร็จ!',
            text: 'รับรางวัลเรียบร้อยแล้ว เครดิตเข้าบัญชีแล้ว',
            icon: 'CheckCircleIcon',
            variant: 'success'
          }
        })

        // Refresh balance
        if (this.$eventBus) {
          this.$eventBus.$emit('refreshBalance')
        }
      } catch (error) {
        console.error('Failed to claim prize:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาด',
            text: error.response?.data?.message || 'ไม่สามารถรับรางวัลได้',
            icon: 'AlertTriangleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isClaiming = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$gold: #ffcc00;
$gold-dark: #ff9900;
$text-primary: #ffffff;
$text-secondary: rgba(255, 255, 255, 0.7);
$card-bg: rgba(255, 255, 255, 0.05);
$border-color: rgba(255, 255, 255, 0.1);

.coupon-lotto-page {
  min-height: 100vh;
  padding: 12px;
  padding-bottom: 100px;

  @media (min-width: 768px) {
    padding: 20px;
    padding-bottom: 100px;
  }
}

// Last Result Banner
.last-result-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: rgba(255, 204, 0, 0.1);
  border: 1px solid rgba(255, 204, 0, 0.3);
  border-radius: 12px;
  cursor: pointer;
  animation: slideDown 0.3s ease;
  gap: 10px;

  &.won {
    background: rgba(34, 197, 94, 0.1);
    border-color: rgba(34, 197, 94, 0.3);
  }

  .banner-content {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  .banner-icon {
    font-size: 1.4rem;
    flex-shrink: 0;
  }

  .banner-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .banner-title {
    font-size: 0.72rem;
    color: $text-secondary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .banner-result {
    font-size: 0.82rem;
    color: $text-primary;
    line-height: 1.3;

    strong {
      color: $gold;
      font-weight: 600;
    }
  }

  .banner-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .banner-claim-badge {
    font-size: 0.7rem;
    font-weight: 600;
    color: #000;
    background: linear-gradient(135deg, $gold, $gold-dark);
    padding: 5px 8px;
    border-radius: 6px;
    cursor: pointer;
    animation: pulse-claim 1.5s ease-in-out infinite;
    white-space: nowrap;
  }

  .banner-close {
    background: none;
    border: none;
    color: $text-secondary;
    font-size: 1.3rem;
    cursor: pointer;
    padding: 2px 6px;
    line-height: 1;

    &:hover {
      color: $text-primary;
    }
  }
}

@keyframes pulse-claim {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 204, 0, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(255, 204, 0, 0);
  }
}

// Waiting Period View
.waiting-period-view {
  .waiting-header {
    text-align: center;
    padding: 20px 15px;
    margin-bottom: 20px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 15px;
  }

  .waiting-title {
    font-size: 1.5rem;
    font-weight: 400;
    color: $gold;
    margin: 0 0 8px 0;
  }

  .waiting-subtitle {
    font-size: 1rem;
    color: $text-secondary;
    margin: 0;
  }

  .waiting-content {
    text-align: center;
    padding: 30px 20px;
    margin-bottom: 20px;
    background: linear-gradient(
      135deg,
      rgba(255, 204, 0, 0.05),
      rgba(255, 153, 0, 0.03)
    );
    border: 1px solid rgba(255, 204, 0, 0.2);
    border-radius: 15px;
  }

  .waiting-animation {
    margin-bottom: 15px;
  }

  .waiting-spinner-icon {
    display: inline-block;
    width: 50px;
    height: 50px;
    border: 3px solid rgba(255, 204, 0, 0.2);
    border-top-color: $gold;
    border-radius: 50%;
    animation: spin 1.2s linear infinite;
  }

  .waiting-message {
    font-size: 1.1rem;
    color: $text-primary;
    margin: 0 0 8px 0;
    font-weight: 500;
  }

  .waiting-hint {
    font-size: 0.85rem;
    color: $text-secondary;
    margin: 0;
  }

  .waiting-selections {
    margin-top: 20px;
    padding: 15px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 15px;

    .section-title {
      margin-bottom: 12px;
    }
  }
}

// Result Period View
.result-period-view {
  .result-header {
    text-align: center;
    padding: 20px 15px;
    margin-bottom: 20px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 15px;
  }

  .result-title {
    font-size: 1.5rem;
    font-weight: 400;
    color: $gold;
    margin: 0 0 8px 0;
  }

  .result-period-date {
    font-size: 1rem;
    color: $text-secondary;
    margin: 0;
  }

  .winning-number-section {
    text-align: center;
    padding: 30px 20px;
    margin-bottom: 20px;
    background: linear-gradient(
      135deg,
      rgba(255, 204, 0, 0.1),
      rgba(255, 153, 0, 0.05)
    );
    border: 1px solid rgba(255, 204, 0, 0.3);
    border-radius: 15px;
  }

  .winning-label {
    font-size: 1.1rem;
    font-weight: 400;
    color: $gold;
    margin: 0 0 15px 0;
  }

  .winning-number-display {
    display: inline-block;
    padding: 20px 40px;
    background: linear-gradient(135deg, $gold, $gold-dark);
    border-radius: 15px;
    box-shadow: 0 0 30px rgba(255, 204, 0, 0.5);
    animation: glow 2s ease-in-out infinite;
  }

  .winning-number {
    font-size: 3rem;
    font-weight: 700;
    color: #000;
    letter-spacing: 10px;
  }

  .my-result-section {
    margin-bottom: 20px;
  }

  .my-result-title {
    font-size: 1rem;
    font-weight: 500;
    color: $text-primary;
    margin: 0 0 12px 0;
    padding: 0 5px;
  }

  .my-result-card {
    padding: 20px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 15px;

    &.won {
      background: rgba(34, 197, 94, 0.1);
      border-color: rgba(34, 197, 94, 0.3);
    }
  }

  .my-selections-label {
    color: $text-secondary;
    font-size: 0.85rem;
    margin-bottom: 10px;
    text-align: center;
  }

  .result-selections-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    margin-bottom: 16px;

    @media (min-width: 380px) {
      grid-template-columns: repeat(6, 1fr);
    }

    @media (min-width: 500px) {
      grid-template-columns: repeat(8, 1fr);
    }
  }

  .result-selection-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 54px;
    padding: 8px 4px;
    background: rgba(255, 204, 0, 0.08);
    border: 1.5px solid rgba(255, 204, 0, 0.25);
    border-radius: 10px;

    .item-number {
      font-size: 1rem;
      font-weight: 700;
      color: $gold;
      line-height: 1.2;
      margin-bottom: 4px;
    }

    .item-status {
      font-size: 0.7rem;
      line-height: 1;
    }

    &.status-won {
      background: rgba(34, 197, 94, 0.15);
      border-color: rgba(34, 197, 94, 0.4);

      .item-number {
        color: #4ade80;
      }
    }

    &.status-claimed {
      background: rgba(34, 197, 94, 0.1);
      border-color: rgba(34, 197, 94, 0.3);

      .item-number {
        color: #4ade80;
      }
    }

    &.status-lost {
      background: rgba(239, 68, 68, 0.08);
      border-color: rgba(239, 68, 68, 0.25);
      opacity: 0.7;

      .item-number {
        color: rgba(255, 255, 255, 0.5);
      }
    }
  }

  .claimed-message {
    text-align: center;
    font-size: 0.9rem;
    color: #4ade80;
    padding: 10px;
    background: rgba(34, 197, 94, 0.1);
    border-radius: 10px;
    margin-top: 12px;
  }

  .result-message {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    padding: 15px;
    border-radius: 10px;

    &.won {
      background: rgba(34, 197, 94, 0.15);
    }

    &.lost {
      background: rgba(239, 68, 68, 0.1);
    }

    .result-text {
      flex: 1;
    }

    .result-main {
      display: block;
      font-size: 1rem;
      font-weight: 500;
      color: $text-primary;
      margin-bottom: 8px;
    }
  }

  // Prize breakdown for split prize display
  .prize-breakdown {
    margin-top: 8px;
    padding: 10px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 8px;
  }

  .prize-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 0.85rem;
    color: $text-secondary;

    .prize-label {
      color: rgba(255, 255, 255, 0.7);
    }

    .prize-value {
      font-weight: 500;
      color: $text-primary;
    }

    &.highlight {
      color: $gold;
      font-weight: 600;
      font-size: 0.95rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      margin-top: 6px;
      padding-top: 8px;

      .prize-label,
      .prize-value {
        color: $gold;
      }
    }
  }

  .claim-btn {
    width: 100%;
    padding: 14px;
    margin-top: 5px;
    background: linear-gradient(135deg, $gold, $gold-dark);
    color: #000;
    border: none;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
    }

    .btn-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
  }

  .result-icon {
    font-size: 2rem;
  }

  .result-text {
    display: flex;
    flex-direction: column;
  }

  .result-main {
    font-size: 1rem;
    font-weight: 500;
    color: $text-primary;
  }

  .result-prize {
    font-size: 1.2rem;
    font-weight: 600;
    color: $gold;
  }

  .result-note {
    font-size: 0.8rem;
    color: $text-secondary;
  }

  .no-selection-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 15px;

    .no-selection-icon {
      font-size: 2rem;
      margin-bottom: 10px;
    }

    .no-selection-text {
      color: $text-secondary;
      font-size: 0.9rem;
    }
  }

  .next-period-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 15px;
    background: $card-bg;
    border: 1px solid $border-color;
    border-radius: 12px;

    .next-period-icon {
      font-size: 1.2rem;
    }

    .next-period-text {
      color: $text-secondary;
      font-size: 0.9rem;
    }
  }
}

// Active Period View
.active-period-view {
  .period-header-card {
    padding: 16px;
    margin-bottom: 16px;
    background: linear-gradient(
      135deg,
      rgba(255, 204, 0, 0.1),
      rgba(255, 153, 0, 0.05)
    );
    border: 1px solid rgba(255, 204, 0, 0.3);
    border-radius: 15px;

    @media (min-width: 768px) {
      padding: 20px;
    }
  }

  .period-header-content {
    text-align: center;
    padding-top: 35px; // Space for info button at top-right
  }

  .period-title {
    font-size: 1.35rem;
    font-weight: 400;
    color: $gold;
    margin: 0 0 8px 0;

    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
  }

  .period-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;

    @media (min-width: 400px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
    }
  }

  .period-info-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;

    @media (min-width: 400px) {
      text-align: left;
    }
  }

  .period-date {
    font-size: 0.95rem;
    color: $text-primary;
  }

  .period-prize {
    font-size: 1.05rem;
    color: $gold;
    font-weight: 500;
  }

  .period-countdown {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    @media (min-width: 400px) {
      align-items: flex-end;
    }
  }

  .countdown-label {
    font-size: 0.72rem;
    color: $text-secondary;
  }

  .countdown-timer {
    display: flex;
    gap: 6px;
  }

  .countdown-unit {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 32px;
    padding: 4px 6px;
    background: rgba(255, 204, 0, 0.1);
    border: 1px solid rgba(255, 204, 0, 0.25);
    border-radius: 6px;

    @media (min-width: 400px) {
      min-width: 36px;
      padding: 5px 8px;
    }
  }

  .countdown-value {
    font-size: 0.95rem;
    font-weight: 700;
    color: $gold;
    line-height: 1.2;

    @media (min-width: 400px) {
      font-size: 1.05rem;
    }
  }

  .countdown-text {
    font-size: 0.58rem;
    color: $text-secondary;
    line-height: 1;

    @media (min-width: 400px) {
      font-size: 0.62rem;
    }
  }
}

// Selection Section
.selection-section {
  margin-bottom: 16px;
}

.selection-form-card,
.already-selected-card,
.no-coupon-card {
  padding: 18px 16px;
  background: $card-bg;
  border: 1px solid $border-color;
  border-radius: 15px;
  text-align: center;

  @media (min-width: 768px) {
    padding: 24px 20px;
  }
}

.form-title,
.card-title {
  font-size: 1.1rem;
  font-weight: 400;
  color: $gold;
  margin: 0 0 8px 0;
}

.coupon-count {
  font-size: 0.9rem;
  color: $text-secondary;
  margin: 0 0 16px 0;

  strong {
    color: $gold;
  }
}

.number-input-wrapper {
  margin-bottom: 16px;
}

.number-input-container {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
}

.digit-input {
  width: 65px;
  height: 75px;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 2.2rem;
  font-weight: 600;
  color: $text-primary;
  text-align: center;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    border-color: $gold;
    box-shadow: 0 0 15px rgba(255, 204, 0, 0.3);
  }

  @media (min-width: 400px) {
    width: 70px;
    height: 80px;
    font-size: 2.5rem;
  }
}

.input-hint {
  font-size: 0.8rem;
  color: $text-secondary;
  margin: 0;
}

.duplicate-warning {
  font-size: 0.85rem;
  color: #fca5a5;
  margin: 0 0 10px 0;
}

.submit-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, $gold, $gold-dark);
  color: #000;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
  }

  .btn-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

.card-message {
  font-size: 0.95rem;
  color: $text-primary;
  margin: 0 0 8px 0;
}

.card-hint {
  font-size: 0.85rem;
  color: $text-secondary;
  margin: 0 0 15px 0;
  line-height: 1.5;
}

.deposit-btn {
  padding: 12px 30px;
  background: linear-gradient(135deg, $gold, $gold-dark);
  color: #000;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
  }
}

// My Selections Section (Compact Grid)
.my-selections-section {
  margin-bottom: 16px;
  padding: 14px;
  background: $card-bg;
  border: 1px solid $border-color;
  border-radius: 15px;

  @media (min-width: 768px) {
    padding: 18px;
  }
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-title {
  font-size: 1rem;
  font-weight: 500;
  color: $text-primary;
  margin: 0;
}

.selection-count {
  font-size: 0.8rem;
  color: $gold;
  background: rgba(255, 204, 0, 0.15);
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
}

// Grid layout for compact number display
.selections-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  margin-bottom: 10px;

  @media (min-width: 380px) {
    grid-template-columns: repeat(6, 1fr);
    gap: 7px;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(8, 1fr);
    gap: 8px;
  }
}

.selection-badge {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 8px 4px;
  background: rgba(255, 204, 0, 0.08);
  border: 1.5px solid rgba(255, 204, 0, 0.25);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  @media (min-width: 380px) {
    min-height: 58px;
    padding: 10px 5px;
  }

  @media (min-width: 768px) {
    min-height: 62px;
    padding: 10px 6px;
  }

  &:active {
    transform: scale(0.95);
  }

  @media (min-width: 768px) {
    &:hover {
      transform: translateY(-2px);
      background: rgba(255, 204, 0, 0.15);
      box-shadow: 0 4px 12px rgba(255, 204, 0, 0.2);
    }
  }

  &.status-won {
    background: rgba(34, 197, 94, 0.15);
    border-color: rgba(34, 197, 94, 0.4);

    .badge-number {
      color: #4ade80;
    }
  }

  &.status-claimed {
    background: rgba(34, 197, 94, 0.1);
    border-color: rgba(34, 197, 94, 0.3);

    .badge-number {
      color: #4ade80;
    }
  }

  &.status-lost {
    background: rgba(239, 68, 68, 0.08);
    border-color: rgba(239, 68, 68, 0.25);
    opacity: 0.7;

    .badge-number {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  &.status-pending {
    animation: subtle-pulse 2s ease-in-out infinite;
  }
}

.badge-number {
  font-size: 1rem;
  font-weight: 700;
  color: $gold;
  line-height: 1.2;
  margin-bottom: 4px;

  @media (min-width: 380px) {
    font-size: 1.05rem;
    margin-bottom: 5px;
  }

  @media (min-width: 768px) {
    font-size: 1.15rem;
    margin-bottom: 6px;
  }
}

.badge-status {
  font-size: 0.7rem;
  line-height: 1;

  @media (min-width: 380px) {
    font-size: 0.75rem;
  }

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }
}

// Legend for status icons
.selections-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px 14px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.68rem;
  color: $text-secondary;

  .legend-icon {
    font-size: 0.72rem;
  }
}

.no-selections {
  padding: 25px;
  text-align: center;

  .no-selections-text {
    color: $text-secondary;
    font-size: 0.9rem;
  }
}

// Selection Detail Modal
.selection-detail-modal {
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  border: 1px solid $border-color;
  border-radius: 20px;
  padding: 25px;
  max-width: 300px;
  width: 100%;
  text-align: center;
  position: relative;
  animation: modalFadeIn 0.3s ease;

  .modal-close {
    position: absolute;
    top: 12px;
    right: 12px;
    background: none;
    border: none;
    color: $text-secondary;
    font-size: 1.4rem;
    cursor: pointer;
    padding: 5px;

    &:hover {
      color: $text-primary;
    }
  }

  .detail-number {
    font-size: 2.5rem;
    font-weight: 700;
    color: $gold;
    background: rgba(255, 204, 0, 0.1);
    padding: 15px 35px;
    border-radius: 12px;
    border: 1px solid rgba(255, 204, 0, 0.3);
    display: inline-block;
    margin-bottom: 20px;
    letter-spacing: 8px;
  }

  .detail-info {
    text-align: left;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    &:last-of-type {
      border-bottom: none;
    }
  }

  .detail-label {
    font-size: 0.85rem;
    color: $text-secondary;
  }

  .detail-value {
    font-size: 0.85rem;
    color: $text-primary;
    font-weight: 500;
  }

  .detail-status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 15px;
    padding: 12px;
    background: rgba(255, 204, 0, 0.1);
    border-radius: 10px;

    &.status-won {
      background: rgba(34, 197, 94, 0.15);
    }

    &.status-claimed {
      background: rgba(34, 197, 94, 0.15);
    }

    &.status-lost {
      background: rgba(239, 68, 68, 0.1);
    }

    .status-icon {
      font-size: 1.2rem;
    }

    .status-text {
      font-size: 0.9rem;
      color: $text-primary;
      font-weight: 500;
    }
  }
}

@keyframes subtle-pulse {
  0%,
  100% {
    border-color: rgba(255, 204, 0, 0.25);
  }
  50% {
    border-color: rgba(255, 204, 0, 0.45);
  }
}

// Last Winner Section
.last-winner-section {
  margin-bottom: 16px;

  .section-title {
    margin-bottom: 10px;
    padding: 0 2px;
  }
}

.last-winner-card {
  padding: 14px;
  background: $card-bg;
  border: 1px solid $border-color;
  border-radius: 12px;
  text-align: center;
}

.winner-period {
  font-size: 0.85rem;
  color: $text-secondary;
  margin-bottom: 10px;
}

.winner-number-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
}

.winner-label {
  font-size: 0.9rem;
  color: $text-secondary;
}

.winner-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: $gold;
  background: rgba(255, 204, 0, 0.1);
  padding: 5px 15px;
  border-radius: 8px;
  border: 1px solid rgba(255, 204, 0, 0.3);
}

.winner-icon {
  font-size: 1.2rem;
}

.winner-name {
  font-size: 0.85rem;
  color: $text-secondary;
}

.winners-list {
  margin-top: 8px;
}

.winners-label {
  font-size: 0.85rem;
  color: $text-secondary;
  margin-bottom: 6px;
}

.winners-names {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}

.winner-name-badge {
  font-size: 0.8rem;
  color: $gold;
  background: rgba(255, 204, 0, 0.1);
  padding: 4px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 204, 0, 0.2);
}

// Modal Styles
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 16px;
}

.confirm-modal,
.result-detail-modal {
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  border: 1px solid $border-color;
  border-radius: 18px;
  padding: 22px 18px;
  max-width: 350px;
  width: 100%;
  text-align: center;
  animation: modalFadeIn 0.3s ease;

  @media (min-width: 400px) {
    padding: 25px 22px;
  }
}

.modal-title {
  font-size: 1.2rem;
  font-weight: 400;
  color: $gold;
  margin: 0 0 15px 0;
}

.modal-number {
  font-size: 3rem;
  font-weight: 700;
  color: $gold;
  background: rgba(255, 204, 0, 0.1);
  padding: 15px 40px;
  border-radius: 15px;
  border: 1px solid rgba(255, 204, 0, 0.3);
  display: inline-block;
  margin-bottom: 15px;
  letter-spacing: 10px;
}

.modal-message {
  font-size: 0.95rem;
  color: $text-primary;
  margin: 0 0 10px 0;

  strong {
    color: $gold;
  }
}

.modal-warning {
  font-size: 0.85rem;
  color: #fca5a5;
  margin: 0 0 20px 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: $text-primary;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
}

.btn-confirm {
  background: linear-gradient(135deg, $gold, $gold-dark);
  border: none;
  color: #000;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: $text-secondary;
  font-size: 1.5rem;
  cursor: pointer;

  &:hover {
    color: $text-primary;
  }
}

.result-detail-modal {
  position: relative;
  max-width: 380px;
  padding: 20px 16px;

  @media (min-width: 400px) {
    padding: 24px 20px;
  }

  .modal-title {
    margin-bottom: 6px;
  }

  .modal-period {
    font-size: 0.85rem;
    color: $text-secondary;
    margin: 0 0 16px 0;
  }

  .modal-winning-number {
    margin-bottom: 16px;

    .label {
      display: block;
      font-size: 0.78rem;
      color: $text-secondary;
      margin-bottom: 6px;
    }

    .number {
      font-size: 2rem;
      font-weight: 700;
      color: $gold;
      background: rgba(255, 204, 0, 0.1);
      padding: 8px 20px;
      border-radius: 10px;
      border: 1px solid rgba(255, 204, 0, 0.3);
      display: inline-block;
      letter-spacing: 6px;
    }
  }

  .modal-selections-section {
    margin-bottom: 12px;

    .label {
      display: block;
      font-size: 0.78rem;
      color: $text-secondary;
      margin-bottom: 8px;
      text-align: left;
    }
  }

  .modal-selections-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    margin-bottom: 12px;

    @media (min-width: 380px) {
      grid-template-columns: repeat(6, 1fr);
    }
  }

  .modal-selection-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1;
    min-height: 44px;
    padding: 5px 3px;
    gap: 2px;
    background: rgba(255, 204, 0, 0.08);
    border: 1.5px solid rgba(255, 204, 0, 0.25);
    border-radius: 8px;
    min-width: 0;

    @media (min-width: 380px) {
      min-height: 48px;
      padding: 6px 4px;
      gap: 3px;
    }

    &.status-won {
      background: rgba(34, 197, 94, 0.15);
      border-color: rgba(34, 197, 94, 0.4);

      .item-number {
        color: #4ade80;
      }
    }

    &.status-claimed {
      background: rgba(34, 197, 94, 0.1);
      border-color: rgba(34, 197, 94, 0.3);

      .item-number {
        color: #4ade80;
      }
    }

    &.status-lost {
      background: rgba(239, 68, 68, 0.08);
      border-color: rgba(239, 68, 68, 0.25);
      opacity: 0.7;

      .item-number {
        color: rgba(255, 255, 255, 0.5);
      }
    }

    .item-number {
      font-size: 0.9rem;
      font-weight: 700;
      color: $gold;
      line-height: 1;

      @media (min-width: 380px) {
        font-size: 0.95rem;
      }
    }

    .item-status {
      font-size: 0.5rem;
      line-height: 1;

      @media (min-width: 380px) {
        font-size: 0.55rem;
      }
    }
  }

  .modal-result-summary {
    padding: 10px 12px;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 10px;
    font-size: 0.85rem;
    color: $text-primary;
    margin-bottom: 12px;
    text-align: center;

    &.won {
      background: rgba(34, 197, 94, 0.1);
    }

    .summary-title {
      display: block;
      font-weight: 500;
      margin-bottom: 6px;
    }
  }

  // Prize breakdown mini for modal
  .prize-breakdown-mini {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    font-size: 0.78rem;
    margin-top: 4px;
    color: rgba(255, 255, 255, 0.7);

    .highlight {
      color: $gold;
      font-weight: 600;
    }
  }

  .modal-claim-section {
    .claim-info {
      font-size: 0.82rem;
      color: $text-secondary;
      margin: 0 0 10px 0;
    }

    .claim-all-btn {
      width: 100%;
      padding: 12px;
      background: linear-gradient(135deg, $gold, $gold-dark);
      color: #000;
      border: none;
      border-radius: 10px;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
      }

      .btn-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }
    }
  }

  .modal-claimed-message {
    padding: 10px 12px;
    background: rgba(34, 197, 94, 0.1);
    border-radius: 10px;
    font-size: 0.85rem;
    color: #4ade80;
    font-weight: 500;
  }
}

// Spinner
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 30px rgba(255, 204, 0, 0.5);
  }
  50% {
    box-shadow: 0 0 50px rgba(255, 204, 0, 0.8);
  }
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// Responsive
@media (min-width: 768px) {
  .coupon-lotto-page {
    max-width: 550px;
    margin: 0 auto;
  }

  .digit-input {
    width: 85px;
    height: 95px;
    font-size: 2.8rem;
  }

  .winning-number {
    font-size: 3.5rem;
    letter-spacing: 12px;
  }
}

// Info button - top right corner (same style as CheckInPage)
.period-header-card {
  position: relative;
}

.info-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
}

// Rules Modal
.rules-modal {
  background: linear-gradient(145deg, #1e1e2e, #2a2a3e);
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
  border: 1px solid rgba(255, 204, 0, 0.3);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.rules-title {
  color: #ffcc00;
  font-size: 1.3rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
}

.rules-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rule-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.05);
  padding: 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.rule-icon {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.rule-text {
  flex: 1;

  strong {
    color: #fff;
    font-size: 0.95rem;
    display: block;
    margin-bottom: 4px;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
    margin: 0;
    line-height: 1.4;
  }
}

.rules-close-btn {
  width: 100%;
  margin-top: 20px;
  padding: 12px;
  background: linear-gradient(135deg, #ffcc00, #ff9900);
  color: #1a1a2e;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 204, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
